import { ApolloError, useLazyQuery } from '@apollo/client'
import { trackError, wait } from '@vend/utilities'
import { isTransactionFinished } from '../../helpers/transaction'
import {
  GET_TRANSACTION,
  Transaction,
  TransactionData,
} from '../../queries/Transaction'
import { SwiperErrorMessage } from '../use-swiper-payment-reducer/types'
import { useErrorMessages } from '../useErrorMessages'

/** one second in milliseconds  */
const SECOND = 1000

/**
 * Mapping a counter (array index) to a timeout length.
 * We're using this and the below default as a sort-of exponential backoff maxing out at 5 sec
 */
const POLLING_TIMEOUT_PER_ATTEMPT = [0.5 * SECOND, 1 * SECOND, 2 * SECOND]

/** the default timeout to apply from the Nth attempt onwards (based on the array having N entries) */
const DEFAULT_POLLING_TIMEOUT = 2 * SECOND

const getPollingTimeout = (retryCounter: number) => {
  // Grab the timeout from the array
  const timeout = POLLING_TIMEOUT_PER_ATTEMPT[retryCounter]

  // Use the default if the array did not specify a timeout for that retry count.
  // Note this is safe because we never have a "zero" (falsy) value timeout.
  return timeout || DEFAULT_POLLING_TIMEOUT
}

// Decide if the Apollo error is recoverable and should keep polling
const isRecoverableApolloError = (error: ApolloError) => {
  const { networkError, graphQLErrors } = error
  // There are two types of ApolloErrors
  // 1. Network error, which means we failed to communicate with GraphQL server
  // https://www.apollographql.com/docs/react/data/error-handling/#network-errors
  if (networkError) {
    // if we receive a ServerError, only retry 5xx errors,
    // as 4xx error means the server does not like the request and no point retrying
    if ('statusCode' in networkError) {
      return networkError.statusCode > 500
    }
    // for any other network errors, e.g. merchant network is down or graphQL server is down, keep polling
    return true
  }

  // 2. GraphQL error, which means we successfully reached the GraphQL server, but something went wrong while the server processes the query
  // https://www.apollographql.com/docs/react/data/error-handling/#graphql-errors
  // if the message indicates status code 5xx in the service behind graphter, we must retry, as we can still successfully recover the result.
  // otherwise, our server returns a "legit" GraphQL error , e.g. cancelled, declined etc, stop polling, and we stop retrying
  return graphQLErrors?.[0].extensions?.status_code?.toString().startsWith('5')
}

/**
 * Hook that will poll for a transaction until it finds it has been completed.
 * @param registerSalePaymentID - the register sale payment id used to query the transaction.
 * @param onComplete - callback called with the finished transaction when polling has finished.
 * @returns A function that starts the polling process.
 */
export const usePollTransaction = (
  registerSalePaymentID: string,
  onComplete: (transaction: Transaction) => void,
  onError: (errorMessage: SwiperErrorMessage) => void
): (() => void) => {
  const { getSwiperErrorMessage } = useErrorMessages()
  const [getTransaction] = useLazyQuery<TransactionData>(GET_TRANSACTION, {
    variables: {
      registerSalePaymentID,
    },
    fetchPolicy: 'network-only',
  })

  const startPolling = () => {
    let attemptCounter = 0

    const pollTransaction = async () => {
      attemptCounter = attemptCounter + 1
      try {
        const { data, error } = await getTransaction()
        if (data?.transaction && isTransactionFinished(data.transaction)) {
          onComplete(data.transaction)
          return
        }
        if (error && !isRecoverableApolloError(error)) {
          onError(getSwiperErrorMessage(error))
          return
        }
        await wait(getPollingTimeout(attemptCounter))
        pollTransaction()
      } catch (error) {
        trackError(
          '[Swiper payment] Unknown error while send GET_TRANSACTION graphQL request',
          { registerSalePaymentID, error }
        )
        await wait(getPollingTimeout(attemptCounter))
        pollTransaction()
      }
    }
    pollTransaction()
  }

  return startPolling
}
